import { useCallback, useEffect, useRef, useState } from "react";
import { messageFor } from "@/lib/errors";
import type { AuthedRequester } from "@/lib/brokerAuth";
import {
  pollBondStepRun,
  runBondStep,
  StepRunStillRunning,
  type BondStepRun,
} from "@/lib/brokerBonds";

// BRK-S196 — start a step's run ONCE per attempt, and poll it.
//
// ── WHY THIS IS NOT A PLAIN EFFECT ───────────────────────────────────────────
//
// Both run surfaces (`DataPullStep`, `PreapprovalChecksStep`) used to POST from
// a `useEffect` whose dependencies included `onSettled` and `authedRequest`.
// The wizard passes the preapproval card an inline `onSettled`, so every parent
// render re-ran the effect, and its `cancelled` flag only muted the state update
// after the await: the POST had already left. On sandbox2 (2026-09-25) that was
// three `POST …/steps/checks/run` inside 85 ms, three Experian pulls, and the
// third refused by Experian's duplicate-request throttle. With real data that
// throttle can turn an approvable request into a referral, and in production a
// pull can be billed.
//
// So an attempt lives OUTSIDE the component, keyed by order + step. A re-render,
// a new requester identity (a token refresh), a new `step` object or a remount
// of the card JOINS the attempt in flight (its latest read, then its polls)
// instead of starting another. Only an explicit Try again starts a new one, and
// it can only follow an attempt that has already ended. The portal holds this
// line itself; surety answering a duplicate with the in-flight run (SUR-S193) is
// a second guard, not the first.

interface Attempt {
  /** The freshest read of the run, for a card that joins mid-attempt. */
  latest: BondStepRun | null;
  listeners: Set<(run: BondStepRun) => void>;
  /** The POST, then the poll to a settled run. */
  done: Promise<BondStepRun>;
}

const inFlight = new Map<string, Attempt>();

/** The attempt in flight for this step, or a new one — never two at once. */
function joinOrStart(
  request: AuthedRequester,
  orderId: string,
  stepKey: string,
): Attempt {
  const key = `${orderId}\u0000${stepKey}`;
  const existing = inFlight.get(key);
  if (existing) return existing;

  const listeners = new Set<(run: BondStepRun) => void>();
  const attempt: Attempt = {
    latest: null,
    listeners,
    done: (async () => {
      const publish = (run: BondStepRun) => {
        attempt.latest = run;
        for (const listener of listeners) listener(run);
      };
      publish(await runBondStep(request, orderId, stepKey));
      // The poll reports every read through `onUpdate`, the settled one too.
      return pollBondStepRun(request, orderId, stepKey, { onUpdate: publish });
    })(),
  };
  inFlight.set(key, attempt);
  // Released the moment it ends, either way — so Try again, which is only ever
  // offered after a failure, starts a fresh run rather than rejoining this one.
  const release = () => {
    if (inFlight.get(key) === attempt) inFlight.delete(key);
  };
  attempt.done.then(release, release);
  return attempt;
}

/**
 * How a card keeps reading a run that outlasted the poll (BRK-S197): a GET every
 * ten seconds for up to ten minutes, and never a POST — the read is free, a
 * second run is a second bureau pull. Cheap enough that a vendor answering late
 * still moves the card on, which is the whole reason to keep reading.
 */
const BACKGROUND_INTERVAL_MS = 10_000;
const BACKGROUND_TIMEOUT_MS = 10 * 60_000;

export interface BondStepRunState {
  run: BondStepRun | null;
  /** PMS's own sentence when the start or the poll failed, else null. */
  error: string | null;
  /**
   * The run had not settled by the poll's deadline (BRK-S197). NOT an error:
   * the card shows the neutral underwriter-review panel and a way on, and this
   * hook keeps reading in the background so a late answer still lands.
   */
  slow: boolean;
  /** Try again: exactly one new run. */
  retry: () => void;
}

export function useBondStepRun(
  request: AuthedRequester,
  orderId: string,
  stepKey: string,
  {
    initialRun,
    onSettled,
  }: {
    /** A run this order already settled, restored from the order (BRK-S088). */
    initialRun?: BondStepRun;
    onSettled: (run: BondStepRun) => void;
  },
): BondStepRunState {
  const [run, setRun] = useState<BondStepRun | null>(initialRun ?? null);
  const [error, setError] = useState<string | null>(null);
  const [slow, setSlow] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // A settled run is a bureau call that already happened — and was already paid
  // for. Resuming replays it; only an explicit Try again re-runs it. Read at
  // MOUNT only: the wizard feeds each settled run back in as `initialRun`, and
  // that prop change must not look like a reason to run again.
  const [replaying] = useState(initialRun !== undefined);
  // Neither of these is a reason to start a run, so neither is a dependency.
  const requestRef = useRef(request);
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    requestRef.current = request;
    onSettledRef.current = onSettled;
  });

  useEffect(() => {
    if (replaying && attempt === 0) return;
    let live = true;
    const background = new AbortController();
    setError(null);
    setSlow(false);
    const current = joinOrStart(requestRef.current, orderId, stepKey);
    if (current.latest) setRun(current.latest);
    const listener = (r: BondStepRun) => {
      if (live) setRun(r);
    };
    current.listeners.add(listener);
    const settle = (settled: BondStepRun) => {
      if (!live) return;
      setSlow(false);
      setRun(settled);
      onSettledRef.current(settled);
    };
    current.done.then(settle, (err: unknown) => {
      if (!live) return;
      if (!(err instanceof StepRunStillRunning)) {
        setError(messageFor(err));
        return;
      }
      // Still going at the deadline: the neutral panel, and a quiet re-read.
      // A failure of THIS read changes nothing on screen — the panel already
      // says the one true thing, and the broker already has a way on.
      setSlow(true);
      pollBondStepRun(requestRef.current, orderId, stepKey, {
        intervalMs: BACKGROUND_INTERVAL_MS,
        timeoutMs: BACKGROUND_TIMEOUT_MS,
        signal: background.signal,
        onUpdate: listener,
      }).then(settle, () => {});
    });
    return () => {
      live = false;
      background.abort();
      current.listeners.delete(listener);
    };
    // `attempt` is the retry trigger.
  }, [orderId, stepKey, attempt, replaying]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { run, error, slow, retry };
}

/**
 * Forget every attempt in flight. A TEST SEAM, called between tests by the
 * global setup: the registry deliberately outlives a component (that is what
 * makes a remount safe), so without it a run one test left polling would be
 * joined by the next test's card on the same order and step.
 */
export function forgetBondStepRuns(): void {
  inFlight.clear();
}
